import type { CSSProperties } from 'react';
import { useDuration } from './shared/useDuration';
import { CachedImage } from './shared/CachedImage';
import { BrandLogo } from './shared/BrandLogo';
import { brandTokens, fontFamilyFor } from './shared/tokens';
import type { ManifestMenuBoardItem } from '../types';

interface MenuClassicProps {
  item: ManifestMenuBoardItem;
  onEnd: () => void;
}

// menu-classic — premium section-based menu layout for restaurants.
//
// Visual rules (per M2E.D scope):
//   - full-bleed background image (with dark overlay) OR solid dark bg
//   - large brand-accented header with menu name + accent bar
//   - sections in a responsive auto-fit grid (1-3 columns by viewport)
//   - per-item rows: name left (with optional badges + description),
//     price right (large, bold, accent font)
//   - brand logo top-right corner
//   - locale-aware price formatting via brand.currency
//
// 16:9 landscape is the design target; portrait still renders cleanly
// because the section grid auto-collapses to a single column.
export function MenuClassic({ item, onEnd }: MenuClassicProps) {
  useDuration(item.durationMs, onEnd);

  const { brand, menu } = item;
  const accent = menu.accentColor ?? brand.primaryColor ?? '#c89a3d';
  const accentFontStack = fontFamilyFor(brand.accentFont);

  return (
    <div style={{ ...rootStyle, ...brandTokens(brand) }}>
      {/* Background layer (image + dark overlay, OR just dark) */}
      {menu.background ? (
        <>
          <CachedImage
            mediaRef={menu.background}
            style={bgImageStyle}
          />
          <div style={bgOverlayStyle} />
        </>
      ) : null}

      {/* Content layer */}
      <div style={contentStyle}>
        <header style={headerStyle}>
          <h1
            style={{
              ...titleStyle,
              fontFamily: accentFontStack,
            }}
          >
            {menu.name}
          </h1>
          <div style={{ ...accentBarStyle, background: accent }} />
        </header>

        <div style={sectionsContainerStyle}>
          {menu.sections.map((section) => (
            <section key={section.sectionId} style={sectionStyle}>
              <h2
                style={{
                  ...sectionTitleStyle,
                  color: accent,
                  fontFamily: accentFontStack,
                }}
              >
                {section.name}
              </h2>
              <ul style={itemListStyle}>
                {section.items.map((it) => (
                  <li key={it.itemId} style={itemRowStyle}>
                    <div style={itemLeftStyle}>
                      <div style={itemNameRowStyle}>
                        <span style={itemNameStyle}>{it.name}</span>
                        {it.badges.length > 0 && (
                          <span style={badgesContainerStyle}>
                            {it.badges.map((b, i) => (
                              <span
                                key={i}
                                style={{ ...badgeStyle, background: accent }}
                              >
                                {b}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      {it.description ? (
                        <div style={itemDescStyle}>{it.description}</div>
                      ) : null}
                    </div>
                    <div
                      style={{
                        ...itemPriceStyle,
                        fontFamily: accentFontStack,
                      }}
                    >
                      {formatPrice(it.priceCents, brand.currency)}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <BrandLogo brand={brand} position="top-right" maxHeight="7vh" />
    </div>
  );
}

// Locale-aware price formatting. Falls back to "1234.56 USD" if the
// browser locale rejects the currency code (shouldn't happen for ISO
// 4217 codes like USD, EUR, GBP — but we never trust input).
function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

// ---------- Styles ----------

const rootStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  background: '#1a1a1a',
  color: '#ffffff',
  overflow: 'hidden',
  fontFamily: '"Inter", system-ui, sans-serif',
};

const bgImageStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0,
};

const bgOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.82) 100%)',
  zIndex: 1,
};

const contentStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '4vh 6vw 3vh',
  boxSizing: 'border-box',
};

const headerStyle: CSSProperties = {
  marginBottom: '3.5vh',
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(2.5rem, 5.5vw, 5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.05,
  color: '#ffffff',
};

const accentBarStyle: CSSProperties = {
  height: '4px',
  width: 'clamp(8rem, 12vw, 16rem)',
  marginTop: '1.6vh',
  borderRadius: '2px',
};

const sectionsContainerStyle: CSSProperties = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 30rem), 1fr))',
  gap: '3vh 4vw',
  alignContent: 'start',
  overflow: 'hidden',
};

const sectionStyle: CSSProperties = {
  minWidth: 0,
};

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 1.6vh',
  fontSize: 'clamp(1.5rem, 2.4vw, 2.4rem)',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const itemListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '1.4vh',
};

const itemRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '1.5vw',
  borderBottom: '1px dashed rgba(255, 255, 255, 0.12)',
  paddingBottom: '1vh',
};

const itemLeftStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const itemNameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.6vw',
};

const itemNameStyle: CSSProperties = {
  fontSize: 'clamp(1.1rem, 1.8vw, 1.7rem)',
  fontWeight: 600,
  lineHeight: 1.2,
};

const badgesContainerStyle: CSSProperties = {
  display: 'inline-flex',
  gap: '0.4vw',
  flexWrap: 'wrap',
};

const badgeStyle: CSSProperties = {
  fontSize: 'clamp(0.7rem, 1vw, 0.95rem)',
  padding: '0.2em 0.75em',
  borderRadius: '999px',
  color: '#000',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: 1.4,
};

const itemDescStyle: CSSProperties = {
  fontSize: 'clamp(0.85rem, 1.2vw, 1.15rem)',
  opacity: 0.7,
  marginTop: '0.4vh',
  fontWeight: 300,
  lineHeight: 1.4,
};

const itemPriceStyle: CSSProperties = {
  fontSize: 'clamp(1.4rem, 2.6vw, 2.6rem)',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  textAlign: 'right',
};
